import React, { useRef, useState, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { classNames, Icon, iconProps, useUpdateRef } from '@zambezi/sdk/common'
import DropdownList from './DropdownList'

/**
 * Create a React Select Dropdown List component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').SelectDropdownListProps<T, Item>} props
 * @param {import('react').Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function selectDropdownList (props, ref) {
  const [selectedItemIndex, setSelectedItemIndex] = useState(
    props.selectedItemIndex === undefined
      ? props.defaultSelectedItemIndex
      : props.selectedItemIndex
  )
  /** @type {import('react').RefObject<HTMLElement>} */
  const dropdownListRef = useRef(null)
  useUpdateRef(ref, dropdownListRef, { focus })
  useEffect(onSelectedItemIndexChange, [props.selectedItemIndex])

  return (
    <DropdownList
      {...props}
      className={classNames('zb-select-dropdown-list', props.className)}
      itemClassName={getItemClassName}
      itemProps={getItemProps}
      renderItem={getRenderItem}
      onItemClick={handleItemClick}
      ref={dropdownListRef}
    />
  )

  function getItemClassName (d, i) {
    const isSelected = i === selectedItemIndex
    return isSelected ? 'zb-select-dropdown-list-item-is-selected' : ''
  }

  function getItemProps (d, i) {
    const isSelected = i === selectedItemIndex
    return {
      'aria-selected': isSelected
    }
  }

  /**
   * @type {import('./interfaces').ItemRenderer<Item>}
   */
  function getRenderItem (d, i) {
    const { renderItem, iconProps } = props
    const isSelected = i === selectedItemIndex

    return (
      <div>
        {renderItem(d, i, isSelected)}

        {isSelected && (
          <Icon
            name='core-dropdown-item-selected'
            title='Selected Item'
            aria-hidden='true'
            iconProps={iconProps}
          />
        )}
      </div>
    )
  }

  /**
   * @param {FocusOptions|number} focusOptions
   */
  function focus (focusOptions) {
    const dropdownList = dropdownListRef.current
    dropdownList && dropdownList.focus(focusOptions)
  }

  function onSelectedItemIndexChange () {
    const { selectedItemIndex } = props
    if (selectedItemIndex !== undefined) {
      setSelectedItemIndex(selectedItemIndex)
    }
  }

  /**
   * @type {import('./interfaces').ItemMouseEventHandler<Item>}
   */
  function handleItemClick (d, i, event) {
    if (props.selectedItemIndex === undefined) {
      setSelectedItemIndex(i)
    }

    props.onItemSelected(d, i, event)
    props.onClose()
  }
}

/**
 * @template T
 * @template Item
 * @type {import('react').ForwardRefExoticComponent<import('./interfaces').SelectDropdownListProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const SelectDropdownList = forwardRef(selectDropdownList)

SelectDropdownList.displayName = 'SelectDropdownList'

SelectDropdownList.propTypes = {
  data: PropTypes.array,
  defaultSelectedItemIndex: PropTypes.number,
  selectedItemIndex: PropTypes.number,
  disabledItemsIndices: PropTypes.array,
  renderItem: PropTypes.func,
  header: PropTypes.element,
  footer: PropTypes.element,
  onItemSelected: PropTypes.func,
  onClose: PropTypes.func,
  arrowPosition: DropdownList.propTypes.arrowPosition,
  arrowOffset: DropdownList.propTypes.arrowOffset,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func,
  iconProps
}

SelectDropdownList.defaultProps = {
  data: [],
  defaultSelectedItemIndex: -1,
  disabledItemsIndices: [],
  renderItem: String,
  onItemSelected: (d, i) => {},
  onClose: () => {},
  renderGroupLabel: String,
  getGroupChildren: () => [],
  onGroupLabelClick: () => {}
}

export default SelectDropdownList
